import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Scene } from '../../types';
import { sceneToPasteBody } from './inline-clipboard';
import { deepClone, getContentItemLabel, getContentItemSummary } from './utils';

export interface InlineClipboardContextValue {
  hasClipboard: boolean;
  clipboardLabel: string;
  copyBlock: (scene: Scene) => void;
  getPasteBody: () => Omit<Scene, 'id'> | null;
}

export const InlineClipboardContext = createContext<InlineClipboardContextValue | null>(null);

export function useInlineClipboard(): InlineClipboardContextValue | null {
  return useContext(InlineClipboardContext);
}

interface InlineClipboardProviderProps {
  children: React.ReactNode;
}

export const InlineClipboardProvider: React.FC<InlineClipboardProviderProps> = ({ children }) => {
  const [clipboardScene, setClipboardScene] = useState<Scene | null>(null);

  const copyBlock = useCallback((scene: Scene) => {
    setClipboardScene(deepClone(scene));
  }, []);

  const getPasteBody = useCallback((): Omit<Scene, 'id'> | null => {
    if (!clipboardScene) return null;
    return sceneToPasteBody(clipboardScene);
  }, [clipboardScene]);

  const clipboardLabel = clipboardScene
    ? `${getContentItemLabel(clipboardScene)} — ${getContentItemSummary(clipboardScene)}`
    : '';

  const value = useMemo(
    () => ({
      hasClipboard: clipboardScene !== null,
      clipboardLabel,
      copyBlock,
      getPasteBody,
    }),
    [clipboardScene, clipboardLabel, copyBlock, getPasteBody]
  );

  return <InlineClipboardContext.Provider value={value}>{children}</InlineClipboardContext.Provider>;
};
